/**
 * Cross-platform geolocation helper.
 *
 * On native Capacitor (Android/iOS) it uses @capacitor/geolocation, which
 * triggers the OS-level permission prompt and uses the native GPS stack.
 * In a browser it falls back to the standard navigator.geolocation API.
 *
 * Includes localStorage-based caching with 5-minute TTL to avoid repeated
 * slow geolocation API calls on desktop and mobile.
 */

/** Minimal position type satisfied by both the browser GeolocationPosition and Capacitor's Position. */
export interface LatLngPosition {
  coords: {
    latitude: number;
    longitude: number;
  };
}

/** Options for getCurrentPosition */
export interface GetCurrentPositionOptions {
  /** Bypass cache and fetch fresh position */
  forceRefresh?: boolean;
}

/** Cached position structure stored in localStorage */
interface CachedPosition {
  coords: { latitude: number; longitude: number };
  timestamp: number;
}

const GEOLOCATION_CACHE_KEY = 'favoris_geolocation_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function getCachedPosition(): LatLngPosition | null {
  if (typeof localStorage === 'undefined') return null;

  const cached = localStorage.getItem(GEOLOCATION_CACHE_KEY);
  if (!cached) return null;

  try {
    const parsed = JSON.parse(cached) as unknown;
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'coords' in parsed &&
      typeof parsed.coords === 'object' &&
      parsed.coords !== null &&
      'latitude' in parsed.coords &&
      'longitude' in parsed.coords &&
      typeof parsed.coords.latitude === 'number' &&
      typeof parsed.coords.longitude === 'number' &&
      'timestamp' in parsed &&
      typeof parsed.timestamp === 'number'
    ) {
      if (Date.now() - parsed.timestamp > CACHE_TTL) {
        localStorage.removeItem(GEOLOCATION_CACHE_KEY);
        return null;
      }

      return {
        coords: {
          latitude: parsed.coords.latitude,
          longitude: parsed.coords.longitude,
        },
      };
    }
    // Invalid structure, remove it
    localStorage.removeItem(GEOLOCATION_CACHE_KEY);
    return null;
  } catch {
    // Invalid cache data, remove it
    localStorage.removeItem(GEOLOCATION_CACHE_KEY);
    return null;
  }
}

function setCachedPosition(position: LatLngPosition): void {
  if (typeof localStorage === 'undefined') return;

  const cache: CachedPosition = {
    coords: position.coords,
    timestamp: Date.now(),
  };
  localStorage.setItem(GEOLOCATION_CACHE_KEY, JSON.stringify(cache));
}

/**
 * Returns the user's current position, requesting native permissions when
 * running inside a Capacitor shell, or using the browser Geolocation API
 * otherwise.
 *
 * Results are cached in localStorage for 5 minutes to avoid repeated
 * slow geolocation API calls.
 *
 * @param options - Optional configuration (forceRefresh bypasses cache)
 * @returns The position on success, null on any error
 */
export async function getCurrentPosition(
  options?: GetCurrentPositionOptions
): Promise<LatLngPosition | null> {
  const { forceRefresh = false } = options ?? {};

  // Check cache first unless force refresh is requested
  if (!forceRefresh) {
    const cached = getCachedPosition();
    if (cached) return cached;
  }
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform()) {
      const { Geolocation } = await import('@capacitor/geolocation');
      const perm = await Geolocation.requestPermissions();
      if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') {
        return null;
      }
      const position = await Geolocation.getCurrentPosition({ timeout: 10000 });
      setCachedPosition(position);
      return position;
    }
  } catch {
    // Not a Capacitor environment or plugin not available — fall through to browser API.
  }

  // Browser fallback
  if (!('geolocation' in navigator)) return null;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCachedPosition(pos);
        resolve(pos);
      },
      () => resolve(null),
      { timeout: 10000 }
    );
  });
}
